import {AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {Forecast} from 'smag-commons/dto/msg';
import {TimeFilter, TimeFilterProfile, TimeFilterType} from '../time-filter/time-filter.component';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../i18n/i18n.service';
import {merge, of, Subject} from 'rxjs';
import {catchError, filter, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {SmagApiService} from '../../api/smag-api.service';
import {MsgBoxService} from '../msg-box.service';
import {getPage, isDefined} from '../../commons/commons';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {ModelDetail, modelDetails} from '../predictions';
import {RadioRateModePipe, UserTypePipe} from '../shared.pipe';
import {EnergyCommunityEntity, EnergyCommunityPod} from 'smag-commons/dto/api';
import {Entity, EntitySubType, EntityType, Pod, PvPlant, WindPlant} from 'smag-commons/cfg/hierarchy';
import {getStatisticalProfileDeviceKeys, hasStatisticalProfile, isConsumer, isProducer, isProsumer} from 'smag-commons/fcn';

export interface ForecastResource {
    resourceId: string;
    name?: string;
    ecResource?: EnergyCommunityEntity;
}

interface Forecasts {
    forecasts: Forecast[];
    total: number;
}

enum ForecastMode {
    MODEL,
    STATISTICAL_PROFILE
}

interface ForecastModel {
    mode: ForecastMode;
    model?: PredictiveModelInfo;
    details?: ModelDetail[];
    statisticalDetail?: string;
}

const DEFAULT_TIME_FILTER: TimeFilterType = TimeFilterType.LAST_24_HOURS;
const PAGE_SIZE = 10;
const PAGE_SIZES = [10, 15, 30, 50];

@Component({
    selector: 'app-forecasts',
    templateUrl: './forecasts.component.html',
    styleUrls: ['./forecasts.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Forecasts per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ForecastsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();
    private timeFilter: TimeFilter;
    private reloadForecasts: Subject<void>;
    private readonly rmp: RadioRateModePipe;
    private readonly utp: UserTypePipe;
    @ViewChildren('paginator', {emitDistinctChangesOnly: true}) private paginator: QueryList<MatPaginator>;

    @Input() resource: ForecastResource;

    forecasts: Forecasts;
    model: ForecastModel;

    // exported in HTML
    TimeFilterProfile = TimeFilterProfile;
    DEFAULT_TIME_FILTER = DEFAULT_TIME_FILTER;
    ForecastMode = ForecastMode;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    isDefined = isDefined;

    constructor(private readonly router: Router,
                private readonly i18n: I18NService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService) {
        this.timeFilter = null;
        this.reloadForecasts = new Subject<void>();
        this.rmp = new RadioRateModePipe();
        this.utp = new UserTypePipe();
        this.forecasts = null;
        this.model = null;
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.paginator.changes.pipe(
            map((query: QueryList<MatPaginator>) => query?.first),
            startWith(this.paginator.first),
            filter(paginator => !!paginator),
            switchMap(paginator => merge(
                paginator.page,
                this.reloadForecasts.pipe(
                    tap(() => paginator.firstPage())
                )
            ).pipe(
                startWith(undefined as unknown),
                map(() => [paginator.pageIndex, paginator.pageSize])
            )),
            takeUntil(this.destroyed)
        ).subscribe(([pageIndex, pageSize]) => {
            this.loadForecasts(pageIndex + 1, pageSize);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('resource' in changes) {
            this.reloadForecasts.next();
            this.loadModel();
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    trackByForecastId(index: number, forecast: Forecast): string {
        return forecast.id;
    }

    isStringValue(value: number | boolean | string) {
        return typeof value === 'string';
    }

    isNumericalValue(value: number | boolean | string) {
        return typeof value === 'number';
    }

    isBooleanValue(value: number | boolean | string) {
        return typeof value === 'boolean';
    }

    onSelectTimeFilter(timeFilter: TimeFilter) {
        this.timeFilter = timeFilter;
        this.reloadForecasts.next();
    }

    onClickGotoForecast(forecastId: string, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.router.navigate(['/c', 'prediction', forecastId], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    private loadModel() {
        this.model = null;
        this.apiService.getEntity(this.resource.resourceId).subscribe(resource => {
            this.model = this.extractModel(this.resource.ecResource, resource);
        });
    }

    private loadForecasts(pageIndex: number, pageSize: number) {
        const {fromTime, toTime} = this.timeFilter.range();

        this.forecasts = null;
        this.apiService.retrieveForecasts([this.resource.resourceId],
            fromTime,
            toTime,
            null,
            false).pipe(
            takeUntil(this.destroyed),
            map(res => ({
                forecasts: getPage(res.results, pageIndex - 1, pageSize),
                total: res.total
            } as Forecasts)),
            catchError(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot retrieve forecasts for resource {0}', this.resource.name || this.resource.resourceId), err);
                return of({forecasts: [], total: 0} as Forecasts);
            })
        ).subscribe(forecasts => {
            this.forecasts = forecasts;
        });
    }

    private extractModel(ecResource: EnergyCommunityEntity, entity: Entity): ForecastModel {
        const predictiveModel = (entity.predictive_models || []).find(m => m?.preferred) ||
            (entity.predictive_models || []).find(m => !(m?.preferred));
        if (predictiveModel) {
            return {
                mode: ForecastMode.MODEL,
                model: predictiveModel,
                details: modelDetails(predictiveModel)
            };
        }
        const keys = getStatisticalProfileDeviceKeys(entity);
        if (keys.map(k => hasStatisticalProfile(entity, k)).reduce((a, b) => a || b, false)) {
            return {
                mode: ForecastMode.STATISTICAL_PROFILE,
                statisticalDetail: this.getStatisticalProfileDetail(ecResource, entity)
            };
        }
        return null;
    }

    private getStatisticalProfileDetail(ecResource: EnergyCommunityEntity, e: Entity) {
        if (e.type === EntityType.PLANT) {
            if (e.sub_type === EntitySubType.PV_PLANT) {
                return this.i18n.translate('Photovoltaic plant') + ', '
                    + this.i18n.translate(this.rmp.transform((e as PvPlant).statistical_profile.rate_mode)).toLowerCase();
            } else if (e.sub_type === EntitySubType.WIND_PLANT) {
                return this.i18n.translate('Wind plant') + ', '
                    + this.i18n.translate(this.rmp.transform((e as WindPlant).statistical_profile.rate_mode)).toLowerCase();
            }
        } else if (e.type === EntityType.POD && !!ecResource) {
            let type = '';
            const pod = ecResource as EnergyCommunityPod;
            if (isConsumer(pod)) {
                type = 'Consumer';
            } else if (isProducer(pod)) {
                type = 'Producer';
            } else if (isProsumer(pod)) {
                type = 'Prosumer';
            }
            return (e as Pod).statistical_profile.user_type ?
                type + ', ' + this.i18n.translate(this.utp.transform((e as Pod).statistical_profile.user_type)).toLowerCase()
                + ', ' + this.i18n.translate(this.rmp.transform((e as Pod).statistical_profile.rate_mode)).toLowerCase() :
                type + ', ' + this.i18n.translate(this.rmp.transform((e as Pod).statistical_profile.rate_mode)).toLowerCase();
        }
    }
}
